"use client";

import NumberInput from "@components/inputs/Number";
import { useMarket } from "@features/market/useMarket";
import * as Slider from "@radix-ui/react-slider";
import type { ChangeEvent, FormEvent } from "react";
import { useMemo } from "react";

type Props<Attribute, Value> = any & {
  min: number;
  max: number;
  values: Value;
  attribute: Attribute;
  onChange: (
    event: ChangeEvent<HTMLInputElement>,
    attribute: Attribute,
  ) => void;
  maxLabel?: string;
  minLabel?: string;
  suffix?: string;
};

export default function Range<Attribute, Value>(
  props: Props<Attribute, Value>,
) {
  const { locale } = useMarket();
  const { min, max, onChange, attribute, values } = props;

  const displayValue = useMemo(() => {
    if (values === undefined) return [min, max];
    return values as number[];
  }, [values, min, max]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.target as HTMLFormElement);
    const minValue = Number(data.get("minValue"));
    const maxValue = Number(data.get("maxValue"));
    const [actualMin, actualMax] = [minValue, maxValue].sort((a, b) => a - b);
    const newValue = {
      target: {
        type: "range",
        min: actualMin,
        max: actualMax,
      },
    } as unknown;
    onChange(newValue as ChangeEvent<HTMLInputElement>, attribute);
  };

  const handleChange = ([minValue, maxValue]: [number, number]) => {
    const newValue = {
      target: {
        type: "range",
        min: minValue,
        max: maxValue,
      },
    } as unknown;
    onChange(newValue as ChangeEvent<HTMLInputElement>, attribute);
  };

  return (
    <div className="pb-4" key={JSON.stringify({ ...displayValue, min, max })}>
      <Slider.Root
        min={min}
        max={max}
        className="relative flex h-3 w-full touch-none items-center select-none"
        defaultValue={displayValue}
        onValueCommit={handleChange}
      >
        <Slider.Track className="relative h-px w-full grow rounded-full bg-black" />
        <Slider.Thumb className="block h-3 w-3 bg-black" />
        <Slider.Thumb className="block h-3 w-3 bg-black" />
      </Slider.Root>
      <form
        onSubmit={handleSubmit}
        className="flex w-full justify-between overflow-hidden"
      >
        <div className="grid shrink gap-y-2">
          <NumberInput
            id="minValue"
            min={min}
            max={max}
            name="minValue"
            label={props.minLabel ?? ""}
            locale={locale}
            alignLabel="left"
            value={displayValue[0]}
            suffix={props.suffix}
          />
        </div>
        <button type="submit" className="invisible" aria-label="Submit" />
        <div className="grid shrink gap-y-2">
          <NumberInput
            id="maxValue"
            min={min}
            max={max}
            name="maxValue"
            label={props.maxLabel ?? ""}
            locale={locale}
            alignLabel="right"
            value={displayValue[1]}
            suffix={props.suffix}
          />
        </div>
      </form>
    </div>
  );
}
